import { Controller } from "@hotwired/stimulus";
import "@github/markdown-toolbar-element";

export default class extends Controller {
  static targets = ["textarea", "preview", "editTab", "previewTab", "toolbar", "attachmentButton", "fileInput"];
  static values = { previewUrl: String, uploadUrl: String };

  connect() {
    this.showEdit();
  }

  showEdit() {
    this.textareaTarget.style.display = "block";
    this.previewTarget.style.display = "none";
    this.toolbarTarget.style.display = "flex";
    this.editTabTarget.classList.add("active");
    this.previewTabTarget.classList.remove("active");
  }

  showPreview() {
    this.textareaTarget.style.display = "none";
    this.previewTarget.style.display = "block";
    this.toolbarTarget.style.display = "none";
    this.editTabTarget.classList.remove("active");
    this.previewTabTarget.classList.add("active");

    this.updatePreview();
  }

  updatePreview() {
    const content = this.textareaTarget.value;
    const previewId = this.previewTarget.id;

    fetch(this.previewUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: `body=${encodeURIComponent(
        content
      )}&element_id=${encodeURIComponent(previewId)}`,
    })
      .then((response) => {
        return response.text();
      })
      .then((html) => {

        // Manually process the Turbo Stream response
        if (window.Turbo) {
          // Create a temporary element to parse the Turbo Stream
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = html;

          // Find and execute turbo-stream elements
          const turboStreams = tempDiv.querySelectorAll("turbo-stream");
          turboStreams.forEach((stream) => {
            const action = stream.getAttribute("action");
            const target = stream.getAttribute("target");
            const template = stream.querySelector("template");

            if (action === "update" && target && template) {
              const targetElement = document.getElementById(target);
              if (targetElement) {
                targetElement.innerHTML = template.innerHTML;
              }
            }
          });
        }
      })
      .catch((error) => {
        console.error("Preview error:", error);
      });
  }

  openFileDialog() {
    if (!this.uploadUrlValue) {
      alert("Cannot upload attachments: newsletter must be saved first");
      return;
    }
    this.fileInputTarget.click();
  }

  handleFileUpload(event) {
    const files = event.target.files;
    if (!files.length) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // If it's an image file, check if it needs resizing
      if (this.isImageFile(file.name)) {
        this.processImageFile(file);
      } else {
        this.uploadFile(file);
      }
    }

    // Clear the input so the same file can be selected again
    event.target.value = '';
  }

  uploadFile(file) {
    if (!this.uploadUrlValue) {
      alert("Cannot upload attachments: newsletter must be saved first");
      return;
    }

    // Show loading state
    this.attachmentButtonTarget.disabled = true;
    this.attachmentButtonTarget.style.opacity = '0.5';

    const formData = new FormData();
    formData.append('file', file);

    fetch(this.uploadUrlValue, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      },
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.insertAttachmentLink(data.attachment);
      } else {
        alert(`Upload failed: ${data.error}`);
        if (data.messages) {
          alert(`Errors: ${data.messages.join(', ')}`);
        }
      }
    })
    .catch(error => {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    })
    .finally(() => {
      // Restore button state
      this.attachmentButtonTarget.disabled = false;
      this.attachmentButtonTarget.style.opacity = '1';
    });
  }

  isImageFile(filename) {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  }

  insertAttachmentLink(attachment) {
    const textarea = this.textareaTarget;
    const cursorPos = textarea.selectionStart;
    const textBefore = textarea.value.substring(0, cursorPos);
    const textAfter = textarea.value.substring(cursorPos);
    
    // Create markdown link or image based on file type
    const markdownLink = this.isImageFile(attachment.filename) 
      ? `![${attachment.filename}](${attachment.url})`
      : `[${attachment.filename}](${attachment.url})`;
    
    // Insert the link at cursor position
    textarea.value = textBefore + markdownLink + textAfter;
    
    // Move cursor to end of inserted link
    textarea.selectionStart = textarea.selectionEnd = cursorPos + markdownLink.length;
    textarea.focus();
  }

  calculateOptimalImageWidth() {
    try {
      // Get the preview container's actual width
      const previewElement = this.previewTarget;
      const containerRect = previewElement.getBoundingClientRect();
      
      // If preview is not visible or has no width, fall back to textarea
      let availableWidth;
      if (containerRect.width > 0) {
        availableWidth = containerRect.width;
      } else {
        // Fallback to textarea width if preview is hidden
        const textareaRect = this.textareaTarget.getBoundingClientRect();
        availableWidth = textareaRect.width;
      }
      
      // Account for padding in preview container (p-3 = 24px total)
      // and add some buffer to prevent edge touching
      const paddingAndBuffer = 48;
      const usableWidth = availableWidth - paddingAndBuffer;
      
      // Set reasonable bounds:
      // Minimum 400px for readability
      // Maximum 1200px to prevent excessively large images
      const minWidth = 400;
      const maxWidth = 1200;
      
      const optimalWidth = Math.min(Math.max(usableWidth, minWidth), maxWidth);
      
      return Math.round(optimalWidth);
      
    } catch (error) {
      console.warn('Failed to calculate optimal image width, using fallback:', error);
      // Fallback to a reasonable default
      return 800;
    }
  }

  processImageFile(file) {
    // Create an image to check dimensions
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      const maxWidth = this.calculateOptimalImageWidth();
      const originalWidth = img.width;
      const originalHeight = img.height;

      // Check if image needs resizing
      if (originalWidth <= maxWidth) {
        // Image is already small enough, upload as-is
        this.uploadFile(file);
        return;
      }

      // Calculate new dimensions maintaining aspect ratio
      const newWidth = maxWidth;
      const newHeight = Math.round((originalHeight * maxWidth) / originalWidth);

      // Set canvas dimensions
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Draw resized image
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Convert canvas to blob
      canvas.toBlob((resizedBlob) => {
        if (!resizedBlob) {
          this.uploadFile(file);
          return;
        }

        // Create a new file with the resized image
        const resizedFile = new File([resizedBlob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });

        // Show resize notification
        this.showResizeNotification(originalWidth, originalHeight, newWidth, newHeight, file.size, resizedFile.size);

        // Upload the resized file
        this.uploadFile(resizedFile);
      }, 'image/jpeg', 0.85); // 85% quality
    };

    img.onerror = () => {
      // Fall back to uploading original file
      this.uploadFile(file);
    };

    // Load the image
    img.src = URL.createObjectURL(file);
  }

  showResizeNotification(originalWidth, originalHeight, newWidth, newHeight, originalSize, newSize) {
    const originalSizeKB = Math.round(originalSize / 1024);
    const newSizeKB = Math.round(newSize / 1024);
    
    // Create a notification that matches the application's toast system
    const notification = document.createElement('div');
    notification.className = 'fixed top-0 left-0 w-full p-4 shadow-sm bg-blue-100 text-blue-800';
    notification.style.zIndex = '9999';
    notification.setAttribute('data-controller', 'collapsible');
    notification.setAttribute('data-collapsible-delay-value', '4000');
    notification.setAttribute('data-collapsible-target', 'content');
    
    notification.innerHTML = `
      <div class="flex justify-between items-center">
        <div>
          <strong class="capitalize">Image Resized:</strong> 
          ${originalWidth}×${originalHeight} → ${newWidth}×${newHeight} (${originalSizeKB}KB → ${newSizeKB}KB)
        </div>
        <button data-action="collapsible#toggle" data-collapsible-target="toggleButton" class="cursor-pointer ml-4 text-blue-800">
          <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="30" height="30" viewBox="0 0 128 128">
            <path d="M 64 6 C 48.5 6 33.9 12 23 23 C 12 33.9 6 48.5 6 64 C 6 79.5 12 94.1 23 105 C 34 116 48.5 122 64 122 C 79.5 122 94.1 116 105 105 C 116 94 122 79.5 122 64 C 122 48.5 116 33.9 105 23 C 94.1 12 79.5 6 64 6 z M 64 12 C 77.9 12 90.900781 17.399219 100.80078 27.199219 C 110.70078 36.999219 116 50.1 116 64 C 116 77.9 110.60078 90.900781 100.80078 100.80078 C 90.900781 110.60078 77.9 116 64 116 C 50.1 116 37.099219 110.60078 27.199219 100.80078 C 17.299219 91.000781 12 77.9 12 64 C 12 50.1 17.399219 37.099219 27.199219 27.199219 C 36.999219 17.299219 50.1 12 64 12 z M 50.5625 47.5 C 49.8 47.5 49.05 47.800391 48.5 48.400391 C 47.3 49.600391 47.3 51.499609 48.5 52.599609 L 59.800781 64 L 48.400391 75.300781 C 47.200391 76.500781 47.200391 78.4 48.400391 79.5 C 49.000391 80.1 49.8 80.400391 50.5 80.400391 C 51.2 80.400391 51.999609 80.1 52.599609 79.5 L 64 68.199219 L 75.300781 79.5 C 75.900781 80.1 76.700391 80.400391 77.400391 80.400391 C 78.100391 80.400391 78.9 80.1 79.5 79.5 C 80.7 78.3 80.7 76.400781 79.5 75.300781 L 68.199219 64 L 79.5 52.699219 C 80.7 51.499219 80.699609 49.600391 79.599609 48.400391 C 78.399609 47.200391 76.500391 47.200391 75.400391 48.400391 L 64 59.800781 L 52.699219 48.400391 C 52.099219 47.800391 51.325 47.5 50.5625 47.5 z"></path>
          </svg>
        </button>
      </div>
    `;
    
    document.body.appendChild(notification);
  }
}
